import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';

const UserSelectionModal = ({
  selectedTasks,
  allUsers,
  selectedEngineers,
  setSelectedEngineers,
  selectedContractors,
  setSelectedContractors,
  setIsAssignUsersModalOpen,
  handleAssignUsersSubmit,
  AllContractors,
  tasks = [] // Add tasks prop to check current assignments
}) => {
  const [localSelectedEngineers, setLocalSelectedEngineers] = useState(selectedEngineers);
  const [localSelectedContractors, setLocalSelectedContractors] = useState(selectedContractors);
  const [showTaskSelection, setShowTaskSelection] = useState(false);
  const [selectedUserForTasks, setSelectedUserForTasks] = useState(null);

  // Update local state when props change
  useEffect(() => {
    setLocalSelectedEngineers(selectedEngineers);
    setLocalSelectedContractors(selectedContractors);
  }, [selectedEngineers, selectedContractors]);

  // Set smart defaults when modal opens
  useEffect(() => {
    if (selectedTasks && selectedTasks.length > 0) {
      const defaults = getSmartDefaultSelections();
      setLocalSelectedEngineers(defaults.engineers);
      setLocalSelectedContractors(defaults.contractors);
    }
  }, [selectedTasks]);

  const getSmartDefaultSelections = () => {
    // Safety check for selectedTasks
    if (!selectedTasks || selectedTasks.length === 0) {
      return { engineers: [], contractors: [] };
    }

    const engineerDefaults = [];
    const contractorDefaults = [];

    // Check engineers - only select if assigned to ALL tasks
    allUsers.forEach((engineer) => {
      const assignedTasks = selectedTasks.filter(
        (task) =>
          task.assigned_engineers &&
          task.assigned_engineers.some((ae) => ae.id === engineer.user.id)
      );

      if (assignedTasks.length === selectedTasks.length) {
        engineerDefaults.push(engineer.user.id);
      }
    });

    // Check contractors - only select if assigned to ALL tasks
    AllContractors.data.forEach((contractor) => {
      const assignedTasks = selectedTasks.filter(
        (task) =>
          task.assigned_contractors &&
          task.assigned_contractors.some((ac) => ac.id === contractor.user.id)
      );

      if (assignedTasks.length === selectedTasks.length) {
        contractorDefaults.push(contractor.user.id);
      }
    });

    return {
      engineers: engineerDefaults,
      contractors: contractorDefaults
    };
  };

  const getUserAssignmentStatus = (userId, userType) => {
    // Safety check for selectedTasks
    if (!selectedTasks || selectedTasks.length === 0) {
      return {
        assignedCount: 0,
        totalCount: 0,
        percentage: 0
      };
    }

    const userTasks = selectedTasks.filter((taskId) => {
      const task = tasks.find((t) => t.id === taskId);
      if (userType === 'engineer') {
        return task?.engineerLiveTaskMapping?.some((eng) => eng.user.id === userId);
      } else {
        return task?.spLiveTaskMapping?.some((sp) => sp.user.id === userId);
      }
    });

    return {
      assignedCount: userTasks.length,
      totalCount: selectedTasks.length,
      percentage:
        selectedTasks.length > 0 ? Math.round((userTasks.length / selectedTasks.length) * 100) : 0
    };
  };

  const toggleEngineerSelection = (userId) => {
    setLocalSelectedEngineers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const toggleContractorSelection = (userId) => {
    setLocalSelectedContractors((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const getStatusBadgeClass = (percentage) => {
    if (percentage === 100) return 'bg-green-100 text-green-800 border-green-200';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleTaskChipClick = (user, userType) => {
    setSelectedUserForTasks({ user, userType });
    setShowTaskSelection(true);
  };

  const handleSubmit = () => {
    // Update parent state
    setSelectedEngineers(localSelectedEngineers);
    setSelectedContractors(localSelectedContractors);

    // Call the submit function
    handleAssignUsersSubmit();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Selected Tasks: {selectedTasks ? selectedTasks.length : 0}
        </h3>
        <p className="text-sm text-gray-600">
          {selectedTasks && selectedTasks.length > 0
            ? 'Assign employees and contractors to the selected tasks. Users assigned to ALL selected tasks are pre-selected. Click on assignment badges to view current task assignments.'
            : 'Please select tasks first to assign users to them.'}
        </p>
      </div>

      {(!selectedTasks || selectedTasks.length === 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-yellow-800 font-medium">No tasks selected</p>
          </div>
          <p className="text-yellow-700 text-sm mt-1">
            Please go back and select the tasks you want to assign users to before proceeding.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {/* Employee Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Employees (Required: 1 Manager + 1 Executive)
          </label>
          <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3">
            {allUsers && allUsers.length > 0 ? (
              allUsers.map((user) => {
                const isSelected = localSelectedEngineers.includes(user.user.id);
                const status = getUserAssignmentStatus(user.user.id, 'engineer');

                // Check if this selection would exceed limits
                let isDisabled = false;
                if (!isSelected) {
                  const selectedEmployeeData = allUsers.filter((u) =>
                    localSelectedEngineers.includes(u.user.id)
                  );
                  const mediumLevelCount = selectedEmployeeData.filter(
                    (u) => u.user.level === 'Medium'
                  ).length;
                  const lowLevelCount = selectedEmployeeData.filter(
                    (u) => u.user.level === 'Low'
                  ).length;

                  if (user.user.level === 'Medium' && mediumLevelCount >= 1) {
                    isDisabled = true;
                  }
                  if (user.user.level === 'Low' && lowLevelCount >= 1) {
                    isDisabled = true;
                  }
                }

                return (
                  <div
                    key={user.user.id}
                    className={`flex items-center justify-between mb-3 p-2 rounded-lg border ${isSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                      }`}>
                    <div className="flex items-center flex-1">
                      <input
                        type="checkbox"
                        id={`engineer-${user.user.id}`}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => toggleEngineerSelection(user.user.id)}
                        className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                      />
                      <label
                        htmlFor={`engineer-${user.user.id}`}
                        className={`ml-2 text-sm cursor-pointer ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
                          }`}>
                        {user.user.name} ({user.user.level === 'Medium' ? 'Manager' : user.user.level === 'Low' ? 'Executive' : user.user.level || 'N/A'})
                      </label>
                    </div>

                    {/* Assignment Status Badge - Clickable */}
                    <div className="ml-3">
                      <button
                        onClick={() => handleTaskChipClick(user.user, 'engineer')}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border cursor-pointer hover:shadow-md transition-all duration-200 ${getStatusBadgeClass(status.percentage)}`}
                        title="Click to manage task assignments">
                        {status.assignedCount}/{status.totalCount} tasks ({status.percentage}%)
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-sm">No employees available</p>
            )}
          </div>
        </div>

        {/* Contractor Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Contractors</label>
          <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3">
            {AllContractors?.data && AllContractors.data.length > 0 ? (
              AllContractors.data.map((contractor) => {
                const isSelected = localSelectedContractors.includes(contractor.user.id);
                const status = getUserAssignmentStatus(contractor.user.id, 'contractor');

                return (
                  <div
                    key={contractor.user.id}
                    className={`flex items-center justify-between mb-3 p-2 rounded-lg border ${isSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                      }`}>
                    <div className="flex items-center flex-1">
                      <input
                        type="checkbox"
                        id={`contractor-${contractor.user.id}`}
                        checked={isSelected}
                        onChange={() => toggleContractorSelection(contractor.user.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`contractor-${contractor.user.id}`}
                        className="ml-2 text-sm text-gray-700 cursor-pointer">
                        {contractor.user.name}
                      </label>
                    </div>

                    {/* Assignment Status Badge - Clickable */}
                    <div className="ml-3">
                      <button
                        onClick={() => handleTaskChipClick(contractor.user, 'contractor')}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border cursor-pointer hover:shadow-md transition-all duration-200 ${getStatusBadgeClass(status.percentage)}`}
                        title="Click to manage task assignments">
                        {status.assignedCount}/{status.totalCount} tasks ({status.percentage}%)
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-sm">No contractors available</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <Button displayType="secondary" onClick={() => setIsAssignUsersModalOpen(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            !selectedTasks ||
            selectedTasks.length === 0 ||
            (localSelectedEngineers.length === 0 && localSelectedContractors.length === 0) ||
            (localSelectedEngineers.length > 0 &&
              (() => {
                const selectedEmployeeData = allUsers.filter((user) =>
                  localSelectedEngineers.includes(user.user.id)
                );
                const mediumLevelCount = selectedEmployeeData.filter(
                  (user) => user.user.level === 'Medium'
                ).length;
                const lowLevelCount = selectedEmployeeData.filter(
                  (user) => user.user.level === 'Low'
                ).length;
                return (
                  (mediumLevelCount !== 1 || lowLevelCount !== 1)
                );
              })())
          }>
          Assign Users
        </Button>
      </div>

      {/* Task Selection Modal */}
      {showTaskSelection && selectedUserForTasks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Task Assignments for {selectedUserForTasks.user.name}
              </h3>
              <button
                onClick={() => setShowTaskSelection(false)}
                className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Tasks currently assigned to this {selectedUserForTasks.userType}:
              </p>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(() => {
                // Get tasks assigned to this user
                const assignedTasks = selectedTasks.filter((taskId) => {
                  const task = tasks.find((t) => t.id === taskId);
                  if (selectedUserForTasks.userType === 'engineer') {
                    return task?.engineerLiveTaskMapping?.some(
                      (eng) => eng.user.id === selectedUserForTasks.user.id
                    );
                  } else {
                    return task?.spLiveTaskMapping?.some(
                      (sp) => sp.user.id === selectedUserForTasks.user.id
                    );
                  }
                });

                if (assignedTasks.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <svg
                        className="w-12 h-12 mx-auto mb-3 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      <p className="text-lg font-medium">No tasks assigned</p>
                      <p className="text-sm">
                        This {selectedUserForTasks.userType} is not currently assigned to any of the
                        selected tasks.
                      </p>
                    </div>
                  );
                }

                return assignedTasks.map((taskId) => {
                  const task = tasks.find((t) => t.id === taskId);

                  return (
                    <div
                      key={taskId}
                      className="flex items-center p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {task?.name || `Task ${taskId}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          {task?.planned_start_date
                            ? `Start: ${new Date(task.planned_start_date).toLocaleDateString()}`
                            : 'No start date'}
                        </p>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t">
              <Button displayType="secondary" onClick={() => setShowTaskSelection(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSelectionModal;
